/**
 * <s-carousel> — a horizontally-scrollable "swim lane" that shows multiple items at once.
 *
 * UI-only: it wires the optional prev/next buttons to scroll the track by roughly one page
 * and toggles their `disabled` state at the start/end of the scroll range. The track is a
 * native scroll-snap container, so scrolling, touch/trackpad swiping, and keyboard focus
 * all work without JS — this component only enhances the arrow controls.
 *
 * Expected DOM (rendered by blocks/carousel.liquid):
 *   <s-carousel>
 *     [<button data-carousel-prev> / <button data-carousel-next>]
 *     <div data-carousel-track> …items… </div>
 *   </s-carousel>
 */
class SCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector("[data-carousel-track]");
    this.prevButton = this.querySelector("[data-carousel-prev]");
    this.nextButton = this.querySelector("[data-carousel-next]");

    if (!this.track) return;

    this.onPrev = () => this.scrollByPage(-1);
    this.onNext = () => this.scrollByPage(1);
    this.onScroll = () => this.updateControls();

    this.prevButton?.addEventListener("click", this.onPrev);
    this.nextButton?.addEventListener("click", this.onNext);
    this.track.addEventListener("scroll", this.onScroll, { passive: true });

    this.resizeObserver = new ResizeObserver(() => this.updateControls());
    this.resizeObserver.observe(this.track);

    if (this.hasAttribute("data-drag")) this.setupDrag();

    this.updateControls();
  }

  disconnectedCallback() {
    this.prevButton?.removeEventListener("click", this.onPrev);
    this.nextButton?.removeEventListener("click", this.onNext);
    this.track?.removeEventListener("scroll", this.onScroll);
    this.resizeObserver?.disconnect();
    this.teardownDrag?.();
  }

  /**
   * Scroll the track by ~80% of its visible width in the given direction.
   * @param {number} direction -1 for previous, 1 for next.
   */
  scrollByPage(direction) {
    const amount = this.track.clientWidth * 0.8 * direction;
    this.track.scrollBy({ left: amount, behavior: "smooth" });
  }


  /* ---- Optional mouse drag (opt-in via the `data-drag` attribute) ----
   * Touch and trackpads already swipe natively; this lets desktop mouse users grab the
   * track and fling it sideways. Snap is paused while dragging and gliding, then restored
   * so the track settles smoothly on the nearest item. A drag never triggers a link click.
   */
  setupDrag() {
    this.reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    this.onPointerDown = (event) => this.startDrag(event);
    this.onPointerMove = (event) => this.moveDrag(event);
    this.onPointerUp = () => this.endDrag();
    this.onDragStart = (event) => event.preventDefault();
    this.onClickCapture = (event) => {
      if (!this.suppressClick) return;
      event.preventDefault();
      event.stopPropagation();
      this.suppressClick = false;
    };
    this.track.addEventListener("pointerdown", this.onPointerDown);
    this.track.addEventListener("dragstart", this.onDragStart);
    this.track.addEventListener("click", this.onClickCapture, true);
  }

  teardownDrag() {
    this.track?.removeEventListener("pointerdown", this.onPointerDown);
    this.track?.removeEventListener("dragstart", this.onDragStart);
    this.track?.removeEventListener("click", this.onClickCapture, true);
    window.removeEventListener("pointermove", this.onPointerMove);
    window.removeEventListener("pointerup", this.onPointerUp);
    window.removeEventListener("pointercancel", this.onPointerUp);
    cancelAnimationFrame(this.glideFrame);
  }

  startDrag(event) {
    if (event.pointerType !== "mouse" || event.button !== 0) return;
    cancelAnimationFrame(this.glideFrame);
    this.dragStartX = event.clientX;
    this.dragStartScroll = this.track.scrollLeft;
    this.lastX = event.clientX;
    this.lastTime = performance.now();
    this.velocity = 0;
    this.moved = false;
    window.addEventListener("pointermove", this.onPointerMove);
    window.addEventListener("pointerup", this.onPointerUp);
    window.addEventListener("pointercancel", this.onPointerUp);
  }

  moveDrag(event) {
    const delta = event.clientX - this.dragStartX;
    if (!this.moved && Math.abs(delta) < 6) return;
    if (!this.moved) {
      this.moved = true;
      this.track.style.scrollSnapType = "none";
      this.track.style.scrollBehavior = "auto";
      this.track.dataset.dragging = "";
    }
    const now = performance.now();
    const elapsed = Math.max(now - this.lastTime, 1);
    this.velocity = (event.clientX - this.lastX) / elapsed;
    this.lastX = event.clientX;
    this.lastTime = now;
    this.track.scrollLeft = this.dragStartScroll - delta;
  }

  endDrag() {
    window.removeEventListener("pointermove", this.onPointerMove);
    window.removeEventListener("pointerup", this.onPointerUp);
    window.removeEventListener("pointercancel", this.onPointerUp);
    if (!this.moved) return;
    this.suppressClick = true;
    setTimeout(() => (this.suppressClick = false), 0);
    delete this.track.dataset.dragging;

    if (this.reduceMotion.matches) return this.settle();

    // Glide: keep moving with the release speed and ease out with friction.
    let velocity = this.velocity * 16;
    const step = () => {
      this.track.scrollLeft -= velocity;
      velocity *= 0.93;
      if (Math.abs(velocity) > 0.4) {
        this.glideFrame = requestAnimationFrame(step);
      } else {
        this.settle();
      }
    };
    this.glideFrame = requestAnimationFrame(step);
  }

  /** Restore snapping so the track eases onto the nearest item. */
  settle() {
    this.track.style.scrollBehavior = "";
    this.track.style.scrollSnapType = "";
  }

  /** Disable prev/next when the track is at its respective end. */
  updateControls() {
    if (!this.prevButton && !this.nextButton) return;

    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    const atStart = this.track.scrollLeft <= 1;
    const atEnd = this.track.scrollLeft >= maxScroll - 1;
    const overflowing = maxScroll > 1;

    if (this.prevButton) this.prevButton.disabled = !overflowing || atStart;
    if (this.nextButton) this.nextButton.disabled = !overflowing || atEnd;
  }
}

if (!customElements.get("s-carousel")) {
  customElements.define("s-carousel", SCarousel);
}
